import { ISelectOption, Select, SelectSummary } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import ContentConstants from '../../contentConstants';
import { IDisplayLogicAttribute, IDisplayLogicCondition } from '../../models';
import { ConditionText, SelectConditionContainer } from '../common';
import { ResponsesContainer, SelectSummaryContainer } from './PreviousQuestionBranchingLogic';

export interface IAttributeBranchingLogicProps {
	unusedAttributes: IDisplayLogicAttribute[];
	condition: IDisplayLogicCondition;
	HandleAttributeChange: (columnIndex: number, attribute: IDisplayLogicAttribute, conditionIndex: number) => void;
	HandleResponseChange: (responses: string[], index: number) => void;
	HandleSelectionDismissal: (conditionIndex: number, values: ISelectOption[]) => void;
	conditionMessages: string[];
	valueMessages: string[];
}

export const AttributeBranchingLogic = (props: IAttributeBranchingLogicProps) => {
	const { conditionMessages, valueMessages, unusedAttributes } = props;
	const { conditionSourceId: selectedAttrId } = props.condition;

	const selectedAttribute = selectedAttrId !== undefined ? getAttributeById(selectedAttrId) : undefined;

	return (
		<div className='row'>
			<ConditionText className='tws-condition-text col-2'>If value of attribute</ConditionText>
			<SelectConditionContainer className='tws-emp-attribute-selector col-5'>
				<Select
					selectWidth='100%'
					displayText={ContentConstants.DISPLAY_LOGIC_ATTRIBUTE_DROPDOWN_TEXT}
					onApply={(sel) => onApplyAttribute(sel as string)}
					selectedValue={selectedAttrId}
					items={getAttributeSelectables()}
					disabled={unusedAttributes.length === 0}
					message={conditionMessages}
					error={conditionMessages.length !== 0}
				/>
			</SelectConditionContainer>
			<ConditionText className='col-1 text-center'>is</ConditionText>
			<ResponsesContainer className='tws-emp-attribute-values-selector col-3'>
				<Select
					displayText={ContentConstants.DISPLAY_LOGIC_ATTRIBUTE_DROPDOWN_VALUES_TEXT}
					onApply={onApplySelection}
					multiple={true}
					disabled={!selectedAttrId || unusedAttributes.length === 0}
					selectedValues={getSelectedResponses(selectedAttribute)}
					items={getAttributeSelections(selectedAttribute)}
					message={valueMessages}
					error={valueMessages.length !== 0}
					showCount={true}
				/>
				<SelectSummaryContainer>
					<SelectSummary
						enableDismiss={true}
						selected={getSelectedResponsesSummary(selectedAttribute)}
						updateSelect={handleSelectionDismissal}
					/>
				</SelectSummaryContainer>
			</ResponsesContainer>
		</div>
	);

	function onApplyAttribute(attrId: string) {
		const attr = getAttributeById(attrId);

		props.HandleAttributeChange(parseInt(attrId, 0), attr, props.condition.index);
	}

	function getAttributeById(attrId: string) {
		const attribute = unusedAttributes.find((attr) => attr.columnIndex.toString() === attrId);
		return attribute as IDisplayLogicAttribute;
	}

	function getAttributeSelectables(): ISelectOption[] {
		if (unusedAttributes.length === 0) {
			return [];
		}

		const selectables = unusedAttributes.map((a) => ({ displayText: a.displayName, name: '' + a.columnIndex }));

		selectables.sort((x, y) => {
			if (x.displayText.toLowerCase() < y.displayText.toLowerCase()) {
				return -1;
			}
			if (x.displayText.toLowerCase() > y.displayText.toLowerCase()) {
				return 1;
			}
			return 0;
		});

		return selectables;
	}

	function onApplySelection(values: string | string[] | undefined) {
		props.HandleResponseChange(values as string[], props.condition.index);
	}

	function getAttributeSelections(forAttribute: IDisplayLogicAttribute | undefined) {
		if (forAttribute) {
			const values = forAttribute.values.map((v) => ({ displayText: v, name: v }));

			values.sort((v1, v2) => {
				if (v1.displayText.toLowerCase() < v2.displayText.toLowerCase()) {
					return -1;
				}
				if (v1.displayText.toLowerCase() > v2.displayText.toLowerCase()) {
					return 1;
				}
				return 0;
			});

			return values;
		}

		return [];
	}

	function getSelectedResponsesSummary(forAttribute: IDisplayLogicAttribute | undefined): ISelectOption[] {
		return getSelectedResponses(forAttribute).map((r) => ({ displayText: r, name: r }));
	}

	function getSelectedResponses(forAttribute: IDisplayLogicAttribute | undefined): string[] {
		if (!forAttribute) {
			return [];
		}

		const selectedResponses = props.condition.displayLogicValues.map((dl) => dl.valueText!);
		const attributeSelections = new Set(forAttribute.values);
		return selectedResponses.filter((response) => attributeSelections.has(response));
	}

	function handleSelectionDismissal(values: ISelectOption[]) {
		props.HandleSelectionDismissal(props.condition.index, values);
	}
};
